import { useMemo } from 'react'
import { useReducedMotion } from 'motion/react'
import type { CustomerProfile } from '../types'
import { useShopI18n } from '../../i18n'
import { buildCustomerAreaNavItems, CustomerAreaLayoutShell } from './customer-area'
import { AddressBookListSection } from './addresses/AddressBookListSection'
import { AddressEditorSection } from './addresses/AddressEditorSection'
import { useCustomerAddressesController } from './addresses/useCustomerAddressesController'

type CustomerAddressesPageProps = {
  profile: CustomerProfile
  onUpdateProfile: (payload: {
    residentialAddress?: string
    billingAddress?: string
    shippingAddress?: string
  }) => Promise<boolean>
  onNavigate: (targetPath: string) => void
}

export const CustomerAddressesPage = ({ profile, onUpdateProfile, onNavigate }: CustomerAddressesPageProps) => {
  const { language, t, toLocalizedPath } = useShopI18n()
  const reducedMotion = Boolean(useReducedMotion())

  const navItems = useMemo(
    () =>
      buildCustomerAreaNavItems({
        t,
        toLocalizedPath,
        language,
      }),
    [language, t, toLocalizedPath],
  )

  const {
    isItalian,
    labels,
    countryOptions,
    countryLabelMap,
    addressBook,
    editorOpen,
    editingAddressId,
    editorState,
    setEditorState,
    saving,
    notice,
    formError,
    resetEditor,
    startCreateAddress,
    startEditAddress,
    submitEditor,
    removeAddress,
    setDefaultRole,
  } = useCustomerAddressesController({
    profile,
    language,
    onUpdateProfile,
  })

  return (
    <CustomerAreaLayoutShell
      eyebrow={t('common.customerArea')}
      title={labels.title}
      subtitle={labels.subtitle}
      navItems={navItems}
      activeNavId="addresses"
      onNavigate={onNavigate}
      actions={[
        {
          id: 'add-address',
          label: labels.addAddress,
          onClick: startCreateAddress,
          variant: 'primary',
        },
        {
          id: 'go-profile',
          label: t('customer.nav.profile'),
          onClick: () => onNavigate(toLocalizedPath('/dashboard/profile')),
          variant: 'secondary',
        },
      ]}
    >
      {notice ? (
        <div className="ca-surface-muted ca-theme-transition rounded-xl px-4 py-2.5 text-sm text-(--color-text-secondary)">
          {notice}
        </div>
      ) : null}

      <AddressBookListSection
        addressBook={addressBook}
        labels={labels}
        countryLabelMap={countryLabelMap}
        saving={saving}
        reducedMotion={reducedMotion}
        onCreateAddress={startCreateAddress}
        onEditAddress={startEditAddress}
        onRemoveAddress={(entryId) => {
          void removeAddress(entryId)
        }}
        onSetDefaultRole={(entryId, role) => {
          void setDefaultRole(entryId, role)
        }}
      />

      <AddressEditorSection
        open={editorOpen}
        editingAddressId={editingAddressId}
        editorState={editorState}
        setEditorState={setEditorState}
        labels={labels}
        countryOptions={countryOptions}
        isItalian={isItalian}
        saving={saving}
        formError={formError}
        reducedMotion={reducedMotion}
        onSubmit={() => {
          void submitEditor()
        }}
        onClose={resetEditor}
        savingLabel={t('customer.addresses.saving')}
      />
    </CustomerAreaLayoutShell>
  )
}
